import React, { useState, useEffect } from "react"
import { Users, Plus, Search, Edit2, Trash2, AlertCircle } from "lucide-react"
import BaseLayout from "../../layouts/BaseLayout"
import AddEmployeeModal from "./compoents/AddEmployeeModal"
import EditEmployeeModal from "./compoents/EditEmployeeModal"
import DeleteEmployeeModal from "./compoents/DeleteEmployeeModal"
import StatusConfirmModal from "./compoents/StatusconfirmModal"
import { useApiHandler } from "../../utils/apiHandler"

function App() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [departmentFilter, setDepartmentFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [employees, setEmployees] = useState([])
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 })
  const apiHandler = useApiHandler()

  // Fetch employee data and stats from API
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await apiHandler.get("/admin/get-all-employees")
        const employeeList = response.data.data.employees || []
        setEmployees(employeeList)

        // Calculate stats
        const activeCount = employeeList.filter((emp) => emp.active).length
        const inactiveCount = employeeList.filter((emp) => !emp.active).length

        setStats({
          total: employeeList.length,
          active: activeCount,
          inactive: inactiveCount,
        })
      } catch (error) {
        console.error("Error fetching employees:", error)
      }
    }

    fetchEmployees()
  }, [])

  // Add new employee
  const handleAddEmployee = async (newEmployee) => {
    try {
      const response = await apiHandler.post("/admin/invite-employee", {
        ...newEmployee,
        active: true, // Default status for new employees
      })

      if (response.data.success) {
        setEmployees((prev) => [response.data.data, ...prev])
        updateStats("add", true)
        setIsAddModalOpen(false) // Only close modal on success
        return { success: true }
      } else {
        return {
          success: false,
          error: response.data.error || "Failed to add employee",
        }
      }
    } catch (error) {
      console.error("Error adding employee:", error)
      return {
        success: false,
        error: error.response?.data?.error || "An error occurred while adding the employee",
      }
    }
  }

  // Edit employee
  const handleEditEmployee = async (updatedEmployee) => {
    try {
      const response = await apiHandler.post(`/admin/employees/${updatedEmployee._id}`, updatedEmployee)
      setEmployees((prev) =>
        prev.map((emp) => (emp._id === updatedEmployee._id ? response.data.data.updatedEmployee : emp)),
      )
      setIsEditModalOpen(false)
      setSelectedEmployee(null)
    } catch (error) {
      console.error("Error updating employee:", error)
    }
  }

  // Delete employee
  const handleDeleteEmployee = async () => {
    try {
      await apiHandler.delete(`/admin/delete-employee/${selectedEmployee._id}`)
      setEmployees((prev) => prev.filter((emp) => emp._id !== selectedEmployee._id))
      updateStats("delete", selectedEmployee.active)
      setIsDeleteModalOpen(false)
      setSelectedEmployee(null)
    } catch (error) {
      console.error("Error deleting employee:", error)
    }
  }

  // Update stats when employee status changes
  const updateStats = (action, status) => {
    setStats((prev) => {
      if (action === "add") {
        return {
          ...prev,
          total: prev.total + 1,
          active: status ? prev.active + 1 : prev.active,
          inactive: !status ? prev.inactive + 1 : prev.inactive,
        }
      } else if (action === "delete") {
        return {
          ...prev,
          total: prev.total - 1,
          active: status ? prev.active - 1 : prev.active,
          inactive: !status ? prev.inactive - 1 : prev.inactive,
        }
      } else if (action === "toggle") {
        return {
          ...prev,
          active: status ? prev.active + 1 : prev.active - 1,
          inactive: status ? prev.inactive - 1 : prev.inactive + 1,
        }
      }
      return prev
    })
  }

  // Toggle employee status
  const handleToggleStatus = async (employee) => {
    try {
      const newStatus = !employee.active
      const response = await apiHandler.put(`/admin/update-employee-status/${employee._id}`, {
        active: newStatus,
      })

      setEmployees((prev) => prev.map((emp) => (emp._id === employee._id ? { ...emp, active: newStatus } : emp)))

      updateStats("toggle", newStatus)
      setIsStatusModalOpen(false)
      setSelectedEmployee(null)
    } catch (error) {
      console.error("Error updating employee status:", error)
    }
  }

  // Filter employees by search, department, and status
  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch = employee.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesDepartment = !departmentFilter || employee.department === departmentFilter
    const matchesStatus = statusFilter === "all" || employee.active.toString() === statusFilter
    return matchesSearch && matchesDepartment && matchesStatus
  })

  return (
    <BaseLayout>
      <div className="min-h-screen bg-slate-50">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Dashboard Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                    <Users className="w-7 h-7 text-blue-600" />
                    Employee Directory
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">Manage your organization's workforce efficiently</p>
                </div>
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <Plus className="w-5 h-5" />
                  Add Employee
                </button>
              </div>

              {/* Search and Filters */}
              <div className="mt-6 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <select
                  className="px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="">All Departments</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Sales">Sales</option>
                  <option value="HR">HR</option>
                  <option value="Finance">Finance</option>
                </select>
                <select
                  className="px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
              {[
                { label: "Total Employees", value: stats.total },
                { label: "Active Employees", value: stats.active, color: "text-green-600" },
                { label: "Inactive Employees", value: stats.inactive, color: "text-red-600" },
              ].map((stat) => (
                <div key={stat.label} className="p-6">
                  <p className="text-sm font-medium text-gray-500">{stat.label}</p>
                  <p className={`mt-2 text-3xl font-semibold ${stat.color || "text-gray-900"}`}>{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Employee List */}
            <div className="px-6 py-4 bg-gray-50/50">
              <h2 className="text-sm font-medium text-gray-500">Employee List</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {filteredEmployees.map((employee, index) => (
                <div
                  key={employee._id || index}
                  className="p-6 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">
                      {employee.name
                        .split(" ")
                        .filter(Boolean)
                        .map((part) => part.charAt(0))
                        .slice(0, 2)
                        .join("")
                        .toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">{employee.name}</h3>
                      <p className="text-sm text-gray-500">{employee.position}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-500">{employee.department}</div>
                    <button
                      onClick={() => {
                        setSelectedEmployee(employee)
                        setIsStatusModalOpen(true)
                      }}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        employee.active
                          ? "bg-green-100 text-green-800 hover:bg-green-200"
                          : "bg-red-100 text-red-800 hover:bg-red-200"
                      }`}
                    >
                      {employee.active ? "Active" : "Inactive"}
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedEmployee(employee)
                          setIsEditModalOpen(true)
                        }}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedEmployee(employee)
                          setIsDeleteModalOpen(true)
                        }}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {filteredEmployees.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                  No employees found matching your criteria
                </div>
              )}
            </div>
          </div>
        </main>

        <AddEmployeeModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAddEmployee={handleAddEmployee}
        />

        <EditEmployeeModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setSelectedEmployee(null)
          }}
          employee={selectedEmployee}
          onEditEmployee={handleEditEmployee}
        />

        <DeleteEmployeeModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false)
            setSelectedEmployee(null)
          }}
          employee={selectedEmployee}
          onDeleteEmployee={handleDeleteEmployee}
        />

        <StatusConfirmModal
          isOpen={isStatusModalOpen}
          onClose={() => {
            setIsStatusModalOpen(false)
            setSelectedEmployee(null)
          }}
          employee={selectedEmployee}
          onConfirm={() => handleToggleStatus(selectedEmployee)}
        />
      </div>
    </BaseLayout>
  )
}

export default App
